import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal } from '@heroui/react';
import type { LocaleCode } from '@/api/models';
import { cn } from '@/utils/cnUtils';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { pairRoleChip } from '../../editor/operationEditorMeta';
import { PairArrow } from '../../editor/components/pairEditor';
import type { PreviewLineBox } from './previewModalMeta';
import { usePreviewModal } from './usePreviewModal';

type PreviewModalProps = {
    /** The row being previewed; `null` closes the modal. */
    operation: { key: string; nameKa: string; nameEn: string } | null;
    locale: LocaleCode;
    /** Whether this session may open the editor. The server decides again either way. */
    canEdit: boolean;
    onOpenChange: (open: boolean) => void;
    onEdit: (key: string) => void;
};

const CAPTION = 'text-[9px] font-bold uppercase tracking-widest text-muted';
const CHIP =
    'inline-flex w-fit items-center rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest';

/**
 * What an operation posts, read-only, from the catalog.
 *
 * The catalog answers *which* operations exist; this answers *what one does* —
 * the question a BranchAdmin about to record one, or an admin about to change
 * one, actually has. Before it, the only way to see a recipe was to open the
 * editor, which is a screen that invites changing what you came to read.
 *
 * Three things it deliberately is not:
 *
 * - **Not the editor's preview panel.** That one runs a recipe against a sample
 *   amount and prints a journal. This one shows no figures at all: it names the
 *   *rule* each pair follows ("amount without VAT"), because a published recipe
 *   has no amount until somebody records it, and a made-up one would be the most
 *   memorable thing on the screen.
 * - **Not a version history.** One version — the latest published — with a link
 *   into the editor. The timeline already has a screen.
 * - **Not offered on a system row.** A code-defined flow has no template to
 *   render; the catalog gives it its prose recipe instead.
 *
 * The boxes are the editor's boxes, down to the debit/credit colours and the
 * dashed-slot treatment, so the recipe reads the same in the place you read it
 * and the place you change it.
 */
export const PreviewModal: React.FC<PreviewModalProps> = ({
    operation,
    locale,
    canEdit,
    onOpenChange,
    onEdit,
}) => {
    const { t } = useTranslation('pages');
    const modal = usePreviewModal({ operationKey: operation?.key ?? null });
    const preview = modal.preview;

    return (
        <Modal isOpen={operation !== null} onOpenChange={onOpenChange}>
            <Modal.Backdrop>
                <Modal.Container size="lg">
                    <Modal.Dialog
                        className="w-full max-w-2xl"
                        {...getNodeTestIdProp('operations-preview-modal')}
                    >
                        <Modal.CloseTrigger />
                        <Modal.Header>
                            <Modal.Heading>
                                <span className="flex flex-col gap-1">
                                    <span>{t('operations.preview.title')}</span>
                                    <span
                                        className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm font-normal text-muted"
                                        {...getNodeTestIdProp(
                                            'operations-preview-subtitle',
                                        )}
                                    >
                                        <span className="text-foreground">
                                            {locale === 'ka'
                                                ? operation?.nameKa
                                                : operation?.nameEn}
                                        </span>
                                        <span>
                                            {locale === 'ka'
                                                ? operation?.nameEn
                                                : operation?.nameKa}
                                        </span>
                                        {preview ? (
                                            <>
                                                <span className="tabular-nums">
                                                    {t(
                                                        'operations.history.versionLabel',
                                                        {
                                                            number: preview.version,
                                                        },
                                                    )}
                                                </span>
                                                <span className="tabular-nums">
                                                    {t(
                                                        'operations.effectiveFrom',
                                                        {
                                                            date: preview.effectiveFromText,
                                                        },
                                                    )}
                                                </span>
                                                <span
                                                    className="inline-flex items-center rounded-lg bg-surface-secondary px-2 py-0.5 text-xs font-medium text-muted"
                                                    {...getNodeTestIdProp(
                                                        'operations-preview-status',
                                                    )}
                                                >
                                                    {t(
                                                        `operations.history.status.${preview.status.toLowerCase()}`,
                                                    )}
                                                </span>
                                            </>
                                        ) : null}
                                    </span>
                                </span>
                            </Modal.Heading>
                        </Modal.Header>

                        <Modal.Body className="flex flex-col gap-3">
                            {modal.hasError ? (
                                <p
                                    className="rounded-xl bg-danger-soft px-4 py-3 text-sm text-danger-soft-foreground"
                                    {...getNodeTestIdProp(
                                        'operations-preview-error',
                                    )}
                                >
                                    {t('operations.preview.error')}
                                </p>
                            ) : modal.isLoading ? (
                                <p
                                    className="text-sm text-muted"
                                    {...getNodeTestIdProp(
                                        'operations-preview-loading',
                                    )}
                                >
                                    {t('operations.preview.loading')}
                                </p>
                            ) : modal.isEmpty || preview === null ? (
                                <p
                                    className="text-sm text-muted"
                                    {...getNodeTestIdProp(
                                        'operations-preview-empty',
                                    )}
                                >
                                    {t('operations.preview.empty')}
                                </p>
                            ) : (
                                <>
                                    <span
                                        className={CAPTION}
                                        {...getNodeTestIdProp(
                                            'operations-preview-pair-count',
                                        )}
                                    >
                                        {t('operations.preview.pairs', {
                                            count: preview.pairs.length,
                                        })}
                                    </span>

                                    {preview.pairs.map((pair) => {
                                        const role = pairRoleChip(pair.role);
                                        return (
                                            <div
                                                className="flex flex-col gap-3 rounded-2xl border border-separator bg-surface p-4"
                                                key={pair.pairGroup}
                                                {...getNodeTestIdProp(
                                                    `operations-preview-pair-${pair.pairGroup}`,
                                                )}
                                            >
                                                <div className="grid grid-cols-1 items-stretch gap-2 sm:grid-cols-[1fr_24px_1fr]">
                                                    <LineBoxRead
                                                        box={pair.debit}
                                                        testId={`${pair.pairGroup}-debit`}
                                                    />
                                                    <PairArrow />
                                                    <LineBoxRead
                                                        box={pair.credit}
                                                        testId={`${pair.pairGroup}-credit`}
                                                    />
                                                </div>

                                                <div className="flex flex-wrap items-center gap-2">
                                                    <span className={CAPTION}>
                                                        {t(
                                                            'operations.editor.pairLabel',
                                                            {
                                                                number: pair.displayNumber,
                                                            },
                                                        )}
                                                    </span>
                                                    <span
                                                        className={cn(
                                                            CHIP,
                                                            role.chip,
                                                        )}
                                                        {...getNodeTestIdProp(
                                                            `operations-preview-role-${pair.pairGroup}`,
                                                        )}
                                                    >
                                                        {t(role.labelKey)}
                                                    </span>
                                                    <span
                                                        className="ms-auto text-xs text-muted"
                                                        {...getNodeTestIdProp(
                                                            `operations-preview-amount-${pair.pairGroup}`,
                                                        )}
                                                    >
                                                        {t(
                                                            'operations.preview.amount',
                                                            {
                                                                value: t(
                                                                    pair.amount
                                                                        .valueKey,
                                                                ),
                                                            },
                                                        )}
                                                        {pair.amount.fraction
                                                            ? ` · ${t(
                                                                  pair.amount
                                                                      .fraction
                                                                      .key,
                                                                  pair.amount
                                                                      .fraction
                                                                      .params,
                                                              )}`
                                                            : ''}
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </>
                            )}
                        </Modal.Body>

                        <Modal.Footer className="items-center">
                            <p
                                className="me-auto max-w-sm text-start text-xs text-muted"
                                {...getNodeTestIdProp(
                                    'operations-preview-note',
                                )}
                            >
                                {t('operations.preview.note')}
                            </p>
                            <Button
                                onPress={() => onOpenChange(false)}
                                variant="outline"
                                {...getNodeTestIdProp(
                                    'operations-preview-close',
                                )}
                            >
                                {t('operations.preview.close')}
                            </Button>
                            {/* Absent, not disabled, for a session that may not
                            edit: the row's Edit button already says editing
                            exists and is not theirs, and repeating that inside a
                            modal they opened to *read* would be a second refusal
                            for an act they did not attempt. */}
                            {canEdit && operation ? (
                                <Button
                                    onPress={() => onEdit(operation.key)}
                                    variant="primary"
                                    {...getNodeTestIdProp(
                                        'operations-preview-edit',
                                    )}
                                >
                                    {t('operations.preview.edit')}
                                </Button>
                            ) : null}
                        </Modal.Footer>
                    </Modal.Dialog>
                </Modal.Container>
            </Modal.Backdrop>
        </Modal>
    );
};

type LineBoxReadProps = {
    box: PreviewLineBox | null;
    testId: string;
};

/**
 * One leg, read-only — the editor's closed `LineBox` without the field.
 *
 * A slot keeps its tinted background and its accent-coloured label, because the
 * single most consequential fact about a published line is whether the account
 * is already decided or is asked for at record time, and that has to survive
 * into a screen whose whole job is to say what this operation posts.
 */
const LineBoxRead: React.FC<LineBoxReadProps> = ({ box, testId }) => {
    const { t } = useTranslation('pages');

    if (!box) {
        return (
            <div
                className="flex flex-col justify-center rounded-2xl border border-dashed border-separator p-3 text-xs text-muted"
                {...getNodeTestIdProp(`operations-preview-line-${testId}`)}
            >
                {t('operations.preview.missingLine')}
            </div>
        );
    }

    return (
        <div
            className={cn(
                'flex flex-col gap-2 rounded-2xl border border-separator bg-surface p-3',
                box.isSlot && 'bg-accent-soft/20',
            )}
            {...getNodeTestIdProp(`operations-preview-line-${testId}`)}
        >
            <span
                className={cn(
                    'text-[9.5px] font-bold uppercase tracking-widest',
                    box.side === 'DEBIT' ? 'text-danger' : 'text-success',
                )}
            >
                {t(
                    box.side === 'DEBIT'
                        ? 'operations.editor.debit'
                        : 'operations.editor.credit',
                )}
            </span>

            <span className="flex flex-col items-start gap-0.5">
                <span className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                    {box.code ? (
                        <span className="text-sm font-bold tabular-nums text-foreground">
                            {box.code}
                        </span>
                    ) : null}
                    {box.name ? (
                        <span className="text-sm text-foreground">
                            {box.name}
                        </span>
                    ) : null}
                </span>
                {box.detail ? (
                    <span
                        className={cn(
                            'text-xs',
                            box.isSlot ? 'text-accent' : 'text-muted',
                        )}
                    >
                        {box.detail}
                    </span>
                ) : null}
            </span>

            <span className="text-xs text-muted">
                {t(box.note.key, box.note.params)}
            </span>
        </div>
    );
};
